/**
 * Team Inbox — conversation-list + thread store (Phase 1).
 *
 * DATA REALITY (why this is a POLL, not onSnapshot): the WhatsApp pipeline
 * collections (`wa_conversations` + its `messages`/turn sources) are
 * SERVER-ONLY by firestore.rules (default-deny) — visibility (admin → all ·
 * staff → own projects · client → rejected) is enforced INSIDE the
 * `waListConversations` callable and the portal must never read the index
 * directly (locked security model; see lib/messagesStore.ts header). So
 * "subscription" here = the proven short-interval poll the live Messages
 * screen ships with, wrapped in an onSnapshot-shaped API (callback +
 * unsubscribe) so Phase-2 can swap in a rules-gated direct listener
 * WITHOUT touching the screens. messagesStore is IMPORTED, never modified
 * (lane rule).
 *
 * V2 fields (`ownerUid` etc.) are TOLERATED when absent — every live row
 * today predates the Phase-2 allocation engine.
 */
import {
  buildForwardAttachment,
  fetchConversations,
  fetchThreadPage,
  markConversationRead,
  reactToMessage,
  resolveTriage as apiResolveTriage,
  sendManualReply,
  sortConversations,
  type ApprovedTemplate,
  type AttachmentInput,
  type ConversationFilter,
  type ConversationRow,
  type ForwardableAttachment,
  type ReactResult,
  type SendResult,
  type ThreadView,
  type WorkspaceMessage,
} from '../../../lib/messagesStore';
import { bubbleKindOf, snippetOf, upgradeTemplateBody } from './snippets';
import { numberForPhoneId, fallbackRegistry } from './numbers';
import {
  assignConversation,
  claimConversation,
  fetchInboxMeta,
  fetchThreadEvents,
  setAvailability as apiSetAvailability,
  upsertCanned,
  upsertRegistry,
  confirmClassification,
  createChatTask,
} from './adminApi';
import type {
  AiProposal,
  CannedReply,
  ClaimResult,
  InboxConversation,
  InboxFilter,
  InboxMessage,
  InboxMeta,
  NumberRegistry,
  ThreadEvent,
} from './types';

/* ───────────────────── pure mapping ───────────────────── */

/**
 * Dialling codes we actually deal with, longest-first so "+971" wins over
 * "+97" and "+91" over "+9". Not a world list — just enough that a number we
 * cannot name still says something TRUE about where it is from.
 */
const DIAL_CODES = [
  '971', '977', '966', '880', '852', '353',
  '91', '31', '44', '49', '61', '65', '60', '81', '86', '27', '33', '39', '41', '46', '47', '64', '20',
  '7', '1',
];

/**
 * PURE — the country code at the front of an all-digit number, or ''.
 */
export function dialCodeOf(phone: string): string {
  const d = String(phone || '').replace(/\D/g, '');
  if (!d) return '';
  for (const cc of DIAL_CODES) {
    if (d.startsWith(cc) && d.length > cc.length) return cc;
  }
  return '';
}

/** True when a "name" is really just a phone number wearing a name's clothes. */
function looksLikeNumber(s: string): boolean {
  const t = String(s || '').trim();
  return !t || /^[\d+\s\-()]+$/.test(t);
}

/**
 * PURE — the avatar label for one row.
 *
 * Triage rows used to be hardcoded to the literal '+9', so a Netherlands
 * number showed a "+91" badge (owner report, 2026-08-02) and a triage row we
 * DID have a name for still showed "+9". Now: initials when we know a name —
 * the WhatsApp profile name counts — otherwise the number's real dialling
 * code, which at least tells the truth about the country.
 */
export function avatarLabel(
  name: string,
  phone: string | null,
  waProfileName?: string | null,
): string {
  const named = !looksLikeNumber(name)
    ? name
    : !looksLikeNumber(waProfileName || '')
      ? String(waProfileName)
      : '';
  if (named) return initialsOf(named);
  const cc = dialCodeOf(phone || name || '');
  return cc ? `+${cc}` : '#';
}

/** Initials for the row avatar ("Atul Sharma" → "AS"; unknowns → "+9…"). */
export function initialsOf(name: string): string {
  return (
    String(name || '')
      .replace(/\+/g, '')
      .split(/[\s_]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('') || '#'
  );
}

/**
 * PURE — map ONE transport message to the bubble view-model. Voice
 * messages: the router writes the Whisper transcript into `body`, so the
 * voice bubble surfaces it as the transcript line (italic, under the
 * player) rather than as plain text.
 */
export function toInboxMessage(
  m: WorkspaceMessage,
  fallbackId: string,
): InboxMessage {
  const raw = m as WorkspaceMessage & {
    kind?: string | null;
    templateName?: string | null;
    templateParams?: string[] | null;
    mentions?: string[];
    // M3.3 classification fields
    aiProposal?: AiProposal | null;
    nodeType?: string | null;
    taskId?: string | null;
    type?: string | null;
    sourceProjectId?: string | null;
    contactId?: string | null;
  };
  const kind = bubbleKindOf({
    body: m.body,
    attachment: m.attachment || null,
    templateName: raw.templateName || null,
    kind: raw.kind || null,
  });
  return {
    id: m.id || fallbackId,
    direction: m.direction,
    who: m.who,
    kind,
    // Upgrade a legacy/echoed [template:NAME] placeholder to the real text.
    // New sends already persist the rendered body; this only repairs history.
    body:
      kind === 'voice'
        ? ''
        : upgradeTemplateBody(
            m.body || '',
            Array.isArray(raw.templateParams) ? raw.templateParams : [],
          ),
    at: m.at,
    deliveryState: m.deliveryState ?? null,
    attachment: m.attachment
      ? {
          kind: m.attachment.kind,
          fileName: m.attachment.fileName,
          contentType: m.attachment.contentType,
          downloadURL: m.attachment.downloadURL,
          // Carry the storage locator through so a bubble can FORWARD the
          // received bytes without re-uploading (server mints a fresh URL
          // from storagePath). Absent on legacy rows → forward falls back to
          // downloadURL, or is blocked when neither is present.
          storagePath: m.attachment.storagePath ?? null,
          storageBucket: m.attachment.storageBucket ?? null,
        }
      : null,
    transcript: kind === 'voice' ? m.body || null : null,
    templateName: raw.templateName || null,
    mentions: Array.isArray(raw.mentions) ? raw.mentions : undefined,
    aiProposal: raw.aiProposal || null,
    nodeType: (raw.nodeType as InboxMessage['nodeType']) || null,
    taskId: raw.taskId || null,
    crossRef: raw.type === 'crossRef',
    sourceProjectId: raw.sourceProjectId || null,
    contactId: raw.contactId || null,
    // AD-028 — the target wamid + pinned reactions map.
    waMessageId: m.waMessageId || null,
    reactions:
      m.reactions && typeof m.reactions === 'object' ? m.reactions : null,
    // WhatsApp location pin (router-threaded structured payload).
    location:
      m.location && typeof m.location === 'object'
        ? {
            lat: typeof m.location.lat === 'number' ? m.location.lat : null,
            lng: typeof m.location.lng === 'number' ? m.location.lng : null,
            name: m.location.name || null,
            address: m.location.address || null,
            mapUrl: m.location.mapUrl || null,
          }
        : null,
    raw: m,
  };
}

/**
 * PURE — map ONE transport conversation row to the S1 view-model.
 * Ownership (`ownerUid`/`ownerName`) is a Phase-2 server field: absent →
 * `owner` stays `undefined` (NO owner chip — pre-V2 row); explicitly null
 * → "Unassigned"; set → the named chip ("You" is decided at render with
 * the signed-in uid).
 */
export function toInboxConversation(
  r: ConversationRow,
  registry: NumberRegistry,
): InboxConversation {
  const raw = r as ConversationRow & {
    ownerUid?: string | null;
    ownerName?: string | null;
  };
  let owner: InboxConversation['owner'];
  if ('ownerUid' in raw && raw.ownerUid !== undefined) {
    owner = raw.ownerUid
      ? { uid: raw.ownerUid, name: raw.ownerName || raw.ownerUid }
      : null;
  }
  return {
    key: r.key,
    name: r.name,
    phone: r.phone,
    initials: r.triage
      ? avatarLabel(r.name, r.phone, r.waProfileName)
      : initialsOf(r.name),
    snippet: r.lastMessage || '—',
    lastMessageAt: r.lastMessageAt,
    unread: r.unread || 0,
    triage: !!r.triage,
    aiHandled: !!r.aiHandled,
    aiBot: r.kind === 'agent' || !!r.aiBot,
    aiPaused: !!r.aiPaused,
    projectId: r.projectId,
    projectRef: r.projectRef,
    contactId: r.contactId ?? null,
    waProfileName: r.waProfileName || null,
    number: numberForPhoneId(registry, r.receivingPhoneNumberId),
    receivingPhoneNumberId: r.receivingPhoneNumberId || null,
    owner,
    raw: r,
  };
}

/**
 * PURE — S1 filter chips (UI Spec §5: All · Mine · Unassigned · Triage)
 * + the independent per-number axis + free-text search.
 *
 *  - mine       → owned by me (Phase 2) OR — until ownership exists —
 *                 rows on a project (the staff list is already
 *                 server-scoped to their projects; same rule the live
 *                 screen ships).
 *  - unassigned → ownership field present AND null. Pre-V2 rows (no
 *                 field) are NOT "unassigned" — the chip stays honest
 *                 until the allocation engine writes ownership.
 */
export function applyInboxFilter(
  rows: InboxConversation[],
  filter: InboxFilter,
  numberId: string | 'all',
  search: string,
  myUid: string | null,
): InboxConversation[] {
  const q = search.trim().toLowerCase();
  return rows.filter((c) => {
    if (filter === 'triage' && !c.triage) return false;
    if (filter === 'unassigned' && c.owner !== null) return false;
    if (filter === 'mine') {
      // Ownership field present → "mine" means owned by ME (an unassigned
      // null is NOT mine). Pre-V2 rows (field absent) → project fallback,
      // same rule the live screen ships.
      const mine =
        c.owner !== undefined
          ? c.owner !== null && c.owner.uid === (myUid || '')
          : !!c.projectId;
      if (!mine) return false;
    }
    if (numberId !== 'all') {
      if ((c.receivingPhoneNumberId || '') !== numberId) return false;
    }
    if (q) {
      // Search extends to email subject + sender + body (spec PART 3.1).
      const em = c.email;
      const hay = `${c.name} ${c.phone || ''} ${c.projectRef || ''} ${c.snippet} ${
        c.subject || ''
      } ${em ? `${em.senderEmail} ${em.bodyText}` : ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/**
 * PURE — filter the conversation list for the FORWARD target picker. Matches
 * on name, phone, project ref and WA profile name; excludes `excludeKey` (the
 * chat the attachment came from — forwarding to itself is a no-op) and email
 * rows (an attachment forward is a WhatsApp media send). Newest-first order is
 * preserved from the input.
 */
export function filterConversationsForForward(
  rows: InboxConversation[],
  search: string,
  excludeKey: string | null,
  limit = 40,
): InboxConversation[] {
  const q = search.trim().toLowerCase();
  const out: InboxConversation[] = [];
  for (const c of rows) {
    if (excludeKey && c.key === excludeKey) continue;
    if (c.channel === 'email') continue;
    if (q) {
      const hay = `${c.name} ${c.phone || ''} ${c.projectRef || ''} ${
        c.waProfileName || ''
      }`.toLowerCase();
      if (!hay.includes(q)) continue;
    }
    out.push(c);
    if (out.length >= limit) break;
  }
  return out;
}

/** PURE — chip counts for the filter bar. */
export function inboxFilterCounts(
  rows: InboxConversation[],
  myUid: string | null,
): { all: number; mine: number; unassigned: number; triage: number; unread: number } {
  return {
    all: rows.length,
    mine: applyInboxFilter(rows, 'mine', 'all', '', myUid).length,
    unassigned: rows.filter((c) => c.owner === null).length,
    triage: rows.filter((c) => c.triage).length,
    unread: rows.reduce((n, c) => n + (c.unread || 0), 0),
  };
}

/**
 * PURE — refresh each row's snippet from its mapped LAST message when the
 * thread is open (the index snippet is plain text; the §7.1 spec wants
 * tick/media prefixes — the list self-upgrades as threads are visited).
 */
export function snippetForMessage(m: InboxMessage): string {
  return snippetOf({
    direction: m.direction,
    body: m.kind === 'voice' ? m.transcript || '' : m.body,
    deliveryState: m.deliveryState,
    kind: m.kind,
    attachment: m.attachment ? { fileName: m.attachment.fileName } : null,
  });
}

/* ───────────────────── data source seam ───────────────────── */

/** Thread payload the screens consume. */
export interface ThreadData {
  key: string;
  messages: InboxMessage[];
  /** Last inbound ms — the session-window anchor (client-computed live). */
  lastInboundMs: number | null;
  phone: string | null;
  /** Whether more older messages exist beyond the current page. */
  hasMore?: boolean;
  /** Oldest message `at` in the current page — pass as `beforeMs` to load older. */
  nextCursor?: number | null;
  /**
   * Out-of-window template picker: the Meta-APPROVED templates the server will
   * allow on this account (server is the source of truth — only names in
   * waClientSend.approvedSet() ship). Empty when none are approved yet.
   */
  approvedTemplates?: ApprovedTemplate[];
  /**
   * The conversation's own row, sent by the server when the requested
   * conversation is NOT on the paginated first page. Without it the thread
   * pane has no row to render against and shows its empty state — which reads
   * as a dead click (owner report, 2026-08-02).
   */
  offPageRow?: InboxConversation | null;
}

/** Result returned by `fetchOlderMessages` — a prepend batch. */
export interface OlderMessagesResult {
  messages: InboxMessage[];
  hasMore: boolean;
  nextCursor: number | null;
}

/**
 * The seam between the screens and the transport. The LIVE source wraps
 * the existing callables; the dev-harness passes a fixture source instead
 * (new-files-only preview, no Firebase). Phase 2 swaps the poll for a
 * rules-gated listener behind this same interface.
 */
export interface InboxDataSource {
  subscribeList(
    cb: (rows: InboxConversation[]) => void,
    onError: (msg: string) => void,
    registry: () => NumberRegistry,
  ): () => void;
  fetchThread(key: string): Promise<ThreadData>;
  /**
   * Fetch a page of messages OLDER than `beforeMs` (the `nextCursor` from
   * the previous load). Called by the thread scroll panel when the user
   * scrolls to the top of the canvas (Module D).
   */
  fetchOlderMessages(key: string, beforeMs: number): Promise<OlderMessagesResult>;
  /** Phase-1 composer: TEXT send only, via the EXISTING callable. */
  sendText(key: string, text: string): Promise<SendResult>;
  /**
   * Out-of-window: send ONE Meta-APPROVED template (the only thing WhatsApp
   * allows when the 24h window is closed). Same single-message callable
   * (waSendManualReply) the text/media path uses — it already accepts
   * `templateName`/`templateParams` and the server re-checks the approved
   * allow-list. The sent template appears in the thread like any outbound.
   */
  sendTemplate(
    key: string,
    templateName: string,
    templateParams: string[],
  ): Promise<SendResult>;
  sendAttachment(
    key: string,
    text: string | undefined,
    attachment: AttachmentInput,
  ): Promise<SendResult>;
  /**
   * FORWARD an already-received attachment to another conversation WITHOUT
   * re-uploading. Reuses the received bytes via `storagePath` (+ `storageBucket`)
   * and the current `downloadURL` — the SAME `sendAttachment` transport, so the
   * forwarded file rides the one-message discipline and the server's 24h-window
   * + visibility re-checks. Returns the send result; throws on transport error.
   */
  forwardAttachment(
    targetKey: string,
    attachment: ForwardableAttachment,
  ): Promise<SendResult>;
  /** AD-028 — react to (or un-react from) ONE message; `emoji:''` removes. */
  sendReaction(
    key: string,
    targetWaMessageId: string,
    emoji: string,
  ): Promise<ReactResult>;
  markRead(key: string): Promise<void>;
  /**
   * Part 3 — clear the SERVER-ONLY conv-index `triage` flag after a WA triage
   * action so the row leaves the Triage filter (the canonical draft_contacts
   * record is resolved separately, Email parity). Best-effort caller; a
   * failure is surfaced so the row is not silently left in triage.
   */
  resolveTriage(key: string, action: string): Promise<{ ok: boolean; changed: boolean }>;
  /* ── Phase-2 ownership surface (teamInboxAdmin callable) ── */
  /** Ownership/claim map for the listed keys + me/teammates/S6 data. */
  fetchMeta(keys: string[]): Promise<InboxMeta>;
  /** §7.3 system lines of one conversation. */
  fetchEvents(key: string): Promise<ThreadEvent[]>;
  assign(
    key: string,
    toStaffId: string,
    note?: string,
  ): Promise<{ ok: boolean; ownerUid: string; ownerName: string }>;
  claim(key: string, focused: boolean): Promise<ClaimResult>;
  setAvailability(available: boolean): Promise<boolean>;
  saveRegistry(value: unknown): Promise<void>;
  saveCanned(items: CannedReply[]): Promise<void>;
  /** M3.3 — confirm (or edit) an AI proposal. Creates a cross-ref on each extra project. */
  confirmProposal(
    messageId: string,
    projectId: string,
    nodeType: string,
    projectIds: string[],
    taskTitle: string | null,
  ): Promise<void>;
  /** M3.3 — dismiss the AI proposal chip without classifying. */
  dismissProposal(messageId: string, projectId: string): Promise<void>;
  /** M3.3 — create a Type-2 chat task; returns the new task id. */
  createTask(
    messageId: string,
    projectId: string,
    contactId: string,
    title: string,
  ): Promise<string>;
}

/** PURE — derive ThreadData from a transport ThreadView. */
export function toThreadData(t: ThreadView): ThreadData {
  const messages = t.messages.map((m, i) => toInboxMessage(m, `m${i}`));
  let lastInboundMs: number | null = null;
  for (const m of messages) {
    if (m.direction === 'in' && typeof m.at === 'number') {
      if (lastInboundMs == null || m.at > lastInboundMs) lastInboundMs = m.at;
    }
  }
  // Server window state is authoritative when present (it may know a
  // newer inbound than the 200-message page shows).
  if (t.window && typeof t.window.lastInboundMs === 'number') {
    lastInboundMs = Math.max(lastInboundMs || 0, t.window.lastInboundMs) || null;
  }
  return {
    key: t.key,
    messages,
    lastInboundMs,
    phone: t.phone,
    hasMore: t.hasMore ?? false,
    nextCursor: t.nextCursor ?? null,
  };
}

/** Poll cadence — matches the proven live screen (Messages.tsx). */
export const LIVE_POLL_MS = 12_000;

/**
 * The production data source: the existing `waListConversations` /
 * `waSendManualReply` / `waMarkConversationRead` callables, list polled at
 * the proven cadence (visibility-aware: paused on a hidden tab).
 */
export function liveDataSource(): InboxDataSource {
  return {
    subscribeList(cb, onError, registry) {
      let stopped = false;
      let inFlight = false;
      async function tick(initial: boolean) {
        if (stopped || inFlight) return;
        if (
          !initial &&
          typeof document !== 'undefined' &&
          document.hidden
        ) {
          return;
        }
        inFlight = true;
        try {
          const res = await fetchConversations();
          if (stopped) return;
          cb(
            sortConversations(res.conversations).map((r) =>
              toInboxConversation(r, registry()),
            ),
          );
        } catch {
          if (initial && !stopped) {
            onError('Could not load conversations. Try again.');
          }
          /* silent poll failure — keep the current list */
        } finally {
          inFlight = false;
        }
      }
      void tick(true);
      const id = window.setInterval(() => void tick(false), LIVE_POLL_MS);
      const onVisible = () => {
        if (typeof document !== 'undefined' && !document.hidden) {
          void tick(false);
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        stopped = true;
        window.clearInterval(id);
        document.removeEventListener('visibilitychange', onVisible);
      };
    },

    async fetchThread(key) {
      const res = await fetchConversations(key);
      if (!res.thread) throw new Error('thread_unavailable');
      return {
        ...toThreadData(res.thread),
        approvedTemplates: Array.isArray(res.approvedTemplates)
          ? res.approvedTemplates
          : [],
        offPageRow: res.offPageConversation
          ? toInboxConversation(res.offPageConversation, fallbackRegistry())
          : null,
      };
    },

    async fetchOlderMessages(key, beforeMs) {
      const page = await fetchThreadPage(key, beforeMs);
      return {
        messages: page.messages.map((m, i) => toInboxMessage(m, `p${i}`)),
        hasMore: page.hasMore,
        nextCursor: page.nextCursor,
      };
    },

    async sendText(key, text) {
      // EXISTING single-message callable — same call shape the live
      // Messages screen uses (one explicit reply; no bulk shape exists).
      return sendManualReply({ conversationKey: key, text });
    },

    async sendTemplate(key, templateName, templateParams) {
      // SAME single-message callable — it already validates `templateName`
      // against the server approved allow-list + renders/persists the real
      // body. No parallel send path (one-message discipline holds).
      return sendManualReply({
        conversationKey: key,
        templateName,
        templateParams,
      });
    },

    async sendAttachment(key, text, attachment) {
      return sendManualReply({ conversationKey: key, text, attachment });
    },

    async forwardAttachment(targetKey, attachment) {
      const input = buildForwardAttachment(attachment);
      if (!input) {
        // No storagePath and no usable URL — the bytes are unreachable, so a
        // forward would be a doomed send. Surface a plain, non-throwing signal
        // the caller can message on.
        throw new Error('forward_unavailable:no_source');
      }
      // SAME single-message media transport as a fresh attach — reuses the
      // received bytes, no re-upload. Server re-gates the 24h window + mints a
      // fresh signed URL from storagePath when `link` is empty.
      return sendManualReply({ conversationKey: targetKey, attachment: input });
    },

    async sendReaction(key, targetWaMessageId, emoji) {
      // AD-028 — ONE explicit reaction on ONE message (or remove if emoji '').
      return reactToMessage({ conversationKey: key, targetWaMessageId, emoji });
    },

    async markRead(key) {
      await markConversationRead(key).catch(() => {
        /* best-effort — never blocks opening (same as live screen) */
      });
    },

    resolveTriage: (key, action) => apiResolveTriage(key, action),

    /* ── Phase-2 ownership surface ── */
    fetchMeta: (keys) => fetchInboxMeta(keys),
    fetchEvents: (key) => fetchThreadEvents(key),
    assign: (key, toStaffId, note) =>
      assignConversation(key, toStaffId, note),
    claim: (key, focused) => claimConversation(key, focused),
    async setAvailability(available) {
      const r = await apiSetAvailability(available);
      return r.available;
    },
    saveRegistry: (value) => upsertRegistry(value),
    saveCanned: (items) => upsertCanned(items),

    async confirmProposal(messageId, projectId, nodeType, projectIds, taskTitle) {
      await confirmClassification({ messageId, projectId, nodeType, projectIds, taskTitle });
    },

    async dismissProposal(messageId, projectId) {
      await confirmClassification({ messageId, projectId, dismiss: true });
    },

    async createTask(messageId, projectId, contactId, title) {
      const r = await createChatTask({ messageId, projectId, contactId: contactId || undefined, title });
      return r.taskId;
    },
  };
}

/**
 * A LENS data source — the Team Inbox's OWN live source, sliced SERVER-SIDE
 * to one contact (Person lens) or one project (Project lens). It delegates
 * EVERY per-thread call (fetchThread / fetchOlderMessages / send* / react /
 * markRead / meta / events / …) to `liveDataSource()` unchanged, and
 * overrides ONLY `subscribeList` so the list is fetched with the server-side
 * filter (`waListConversations` `contactId`/`projectId`). This is the whole
 * point of the P3 rework: the Contact "Communications" tab and the Project
 * conversation are FILTERED VIEWS of the one live `wa_conversations` store —
 * never a parallel copy that can drift.
 *
 * A CLIENT-side filter would be wrong: `subscribeList` reads only the newest
 * page of conversations, so a contact whose last message is older than that
 * page would show an empty tab. The filter therefore MUST be server-side.
 * Filtered mode returns up to 200 rows with a null cursor (no pagination),
 * so the single-shot poll below mirrors the live list cadence exactly.
 */
export function lensDataSource(filter: ConversationFilter): InboxDataSource {
  const live = liveDataSource();
  return {
    ...live,
    subscribeList(cb, onError, registry) {
      let stopped = false;
      let inFlight = false;
      async function tick(initial: boolean) {
        if (stopped || inFlight) return;
        if (
          !initial &&
          typeof document !== 'undefined' &&
          document.hidden
        ) {
          return;
        }
        inFlight = true;
        try {
          const res = await fetchConversations(undefined, undefined, filter);
          if (stopped) return;
          cb(
            sortConversations(res.conversations).map((r) =>
              toInboxConversation(r, registry()),
            ),
          );
        } catch {
          if (initial && !stopped) {
            onError('Could not load conversations. Try again.');
          }
          /* silent poll failure — keep the current list */
        } finally {
          inFlight = false;
        }
      }
      void tick(true);
      const id = window.setInterval(() => void tick(false), LIVE_POLL_MS);
      const onVisible = () => {
        if (typeof document !== 'undefined' && !document.hidden) {
          void tick(false);
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        stopped = true;
        window.clearInterval(id);
        document.removeEventListener('visibilitychange', onVisible);
      };
    },
  };
}
